import { z } from 'zod';
import {
  AREA_STANDARDS,
  AREA_UNITS,
  CHARGE_TYPES,
  CONNECTOR_HEALTH,
  COST_CLASSES,
  DATA_ISSUE_CATEGORIES,
  DATA_ISSUE_KINDS,
  DATA_ISSUE_STATUSES,
  DEPOSIT_STATUSES,
  DOCUMENT_CATEGORIES,
  EJAR_STATUSES,
  ESCALATION_TYPES,
  EVENT_AUDIENCES,
  EVENT_DATE_CERTAINTY,
  EVENT_KINDS,
  EVENT_PROPOSAL_STATUSES,
  EVIDENCE_CLASSES,
  EXPENSE_CATEGORIES,
  EXPENSE_STATUSES,
  INBOX_CATEGORIES,
  INBOX_PROVIDERS,
  INBOX_REPLY_STATES,
  INBOX_LINK_ENTITY_TYPES,
  INBOX_STATUSES,
  LEASE_STATUSES,
  LEDGER_TYPES,
  OCCUPANCY_STATUSES,
  OPTION_HOLDERS,
  OPTION_TYPES,
  PARTY_FORMS,
  PARTY_TYPES,
  PAYMENT_FREQUENCIES,
  PAYMENT_METHODS,
  PRIORITIES,
  PROPERTY_TYPES,
  PROSPECT_SOURCES,
  PROSPECT_STAGES,
  PROSPECT_TOUCH_KINDS,
  RECURRENCE_CALENDARS,
  REGISTRATION_TYPES,
  REVIEW_STATES,
  ROLES,
  SOURCE_KINDS,
  TASK_STATUSES,
  TASK_TYPES,
  UNIT_CATEGORIES,
} from './domain';
import { MAX_HALALAS } from './money';

/**
 * Request-boundary validation.
 *
 * Every write endpoint parses its body through one of these before the service
 * layer sees it. Nothing reaches the database untyped, and the database's CHECK
 * constraints are the second line of defence — not the first, and not the only.
 */

export const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'التاريخ يجب أن يكون بصيغة YYYY-MM-DD')
  .refine((v) => {
    const d = new Date(`${v}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
  }, 'التاريخ غير صالح');

/** Money at the boundary is always an integer number of halalas. */
export const halalas = z
  .number()
  .int('المبلغ يجب أن يكون عددًا صحيحًا من الهللات')
  .min(0, 'المبلغ لا يمكن أن يكون سالبًا')
  .max(MAX_HALALAS, 'المبلغ خارج النطاق المسموح');

/** Arabic free text. Trimmed, length-capped, never empty when required. */
const arText = (max = 2000) => z.string().trim().max(max);
const arRequired = (max = 500) => z.string().trim().min(1, 'هذا الحقل مطلوب').max(max);

/**
 * An IDENTIFIER. Deliberately permissive about punctuation — slashes, dashes
 * and leading zeroes are significant — but it must not be empty, start with
 * whitespace, or contain control characters that would make two visibly
 * identical values compare unequal.
 */
const identifier = z
  .string()
  .trim()
  .min(1)
  .max(120)
  .refine((v) => !/\p{Cc}/u.test(v), 'المعرّف يحتوي على رموز غير صالحة');

export const versioned = z.object({ version: z.number().int().min(1) });

// ── Properties and units ─────────────────────────────────────────────────────

export const propertyCreateSchema = z.object({
  code: identifier,
  nameAr: arRequired(200),
  aliases: z.array(arText(200)).max(20).optional(),
  propertyType: z.enum(PROPERTY_TYPES).default('retail'),
  city: arText(100).optional(),
  district: arText(100).optional(),
  addressAr: arText(500).optional(),
  titleDeedNumber: identifier.optional(),
  nationalAddress: identifier.optional(),
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
  declaredUnitCount: z.number().int().min(0).max(100000).optional(),
  declaredUnitCountNote: arText().optional(),
  acquiredOn: isoDate.optional(),
  notes: arText().optional(),
});
export const propertyUpdateSchema = propertyCreateSchema.partial().merge(versioned);

export const unitCreateSchema = z.object({
  propertyId: z.string().min(1),
  buildingId: z.string().optional(),
  unitNumber: identifier,
  sourceLabel: arText(200).optional(),
  category: z.enum(UNIT_CATEGORIES).default('other'),
  floor: arText(20).optional(),
  occupancyStatus: z.enum(OCCUPANCY_STATUSES).default('unknown'),
  notes: arText().optional(),
});
export const unitUpdateSchema = unitCreateSchema
  .partial()
  .omit({ propertyId: true })
  .merge(versioned);

export const unitAreaCreateSchema = z.object({
  unitId: z.string().min(1),
  standard: z.enum(AREA_STANDARDS),
  unit: z.enum(AREA_UNITS).default('sqm'),
  value: z.number().positive('المساحة يجب أن تكون موجبة').max(10_000_000),
  componentsJson: z.record(z.number()).optional(),
  measuredOn: isoDate.optional(),
  measuredBy: arText(200).optional(),
  sourceId: z.string().optional(),
  notes: arText().optional(),
});
export const unitAreaUpdateSchema = unitAreaCreateSchema
  .omit({ unitId: true })
  .partial()
  .merge(versioned);

// ── Parties ──────────────────────────────────────────────────────────────────

export const partyCreateSchema = z.object({
  nameAr: arRequired(300),
  nameLatin: z.string().trim().max(300).optional(),
  partyTypes: z.array(z.enum(PARTY_TYPES)).min(1).default(['tenant']),
  partyForm: z.enum(PARTY_FORMS).default('unknown'),
  identityStatus: z
    .enum(['confirmed', 'probable', 'unresolved', 'duplicate'])
    .default('unresolved'),
  /** One of the owner's own companies — see parties.is_group_company. */
  isGroupCompany: z.boolean().default(false),
  creditNotes: arText().optional(),
  notes: arText().optional(),
});
export const partyUpdateSchema = partyCreateSchema.partial().merge(versioned);

export const partyRegistrationSchema = z.object({
  partyId: z.string().min(1),
  registrationType: z.enum(REGISTRATION_TYPES),
  value: identifier,
  issuedOn: isoDate.optional(),
  expiresOn: isoDate.optional(),
  notes: arText().optional(),
});
export const partyRegistrationUpdateSchema = partyRegistrationSchema
  .omit({ partyId: true, registrationType: true })
  .partial()
  .merge(versioned);

/**
 * A person at a counterparty.
 *
 * `email` is load-bearing beyond the address book: `services/gmailSync.ts`
 * matches an incoming message to a party by an EXACT, lower-cased match
 * against this column. It is the connector's most reliable rule and the only
 * one that does not depend on somebody writing a name or a contract number
 * into a subject line — so an address recorded here is the difference between
 * a tenant's mail filing itself and a person doing it by hand.
 *
 * Both optional fields mirror a CHECK constraint on the table: the phone is
 * E.164 (`+` then digits), and an address that cannot be an address is
 * refused rather than stored as something that will never match.
 */
export const partyContactSchema = z.object({
  partyId: z.string().min(1),
  nameAr: arRequired(200),
  role: arText(120).optional(),
  phone: z
    .string()
    .trim()
    .regex(/^\+[0-9]{6,20}$/, 'الجوال يجب أن يبدأ بـ + ثم أرقام فقط، مثل ‎+9665xxxxxxxx')
    .optional(),
  email: z.string().trim().toLowerCase().email('البريد الإلكتروني غير صالح').max(200).optional(),
  isNoticeRecipient: z.boolean().default(false),
  isPrimary: z.boolean().default(false),
  notes: arText().optional(),
});
export const partyContactUpdateSchema = partyContactSchema
  .omit({ partyId: true })
  .partial()
  .merge(versioned);

// ── Leases ───────────────────────────────────────────────────────────────────

const leaseFields = z.object({
  contractNumber: identifier,
  ejarContractNumber: identifier.optional(),
  ejarStatus: z.enum(EJAR_STATUSES).default('not_registered'),
  ejarRegisteredOn: isoDate.optional(),
  ejarExpiresOn: isoDate.optional(),
  ejarNotes: arText().optional(),

  propertyId: z.string().min(1),
  tenantId: z.string().min(1),
  unitIds: z.array(z.string()).max(200).optional(),
  status: z.enum(LEASE_STATUSES).default('draft'),

  startDate: isoDate,
  endDate: isoDate,
  occupationDate: isoDate.optional(),
  rentCommencementDate: isoDate.optional(),
  rentFreeMonths: z.number().min(0).max(120).default(0),

  annualBaseRentHalalas: halalas.optional(),
  annualDiscountHalalas: halalas.default(0),
  annualServiceChargeHalalas: halalas.default(0),
  annualVatHalalas: halalas.default(0),
  annualTotalHalalas: halalas.optional(),
  vatRatePercent: z.number().min(0).max(100).default(15),

  paymentFrequency: z.enum(PAYMENT_FREQUENCIES).default('annual'),
  paymentsPerYear: z.number().int().min(1).max(366).optional(),
  paymentDueDays: z.number().int().min(0).max(365).default(0),
  firstDueDate: isoDate.optional(),
  hasIrregularInstalments: z.boolean().default(false),

  escalationType: z.enum(ESCALATION_TYPES).default('none'),
  escalationPercent: z.number().min(-100).max(1000).optional(),
  escalationAmountHalalas: halalas.optional(),
  escalationEveryMonths: z.number().int().min(1).max(120).optional(),
  escalationNotes: arText().optional(),

  hasTurnoverRent: z.boolean().default(false),
  turnoverRentPercent: z.number().min(0).max(100).optional(),
  turnoverBreakpointHalalas: halalas.optional(),
  turnoverReportingFrequency: z.enum(PAYMENT_FREQUENCIES).optional(),

  serviceChargeBasis: arText(500).optional(),
  serviceChargeCapHalalas: halalas.optional(),
  noticePeriodDays: z.number().int().min(0).max(1095).optional(),
  renewalNoticeDays: z.number().int().min(0).max(1095).optional(),
  obligationsJson: z.record(z.enum(['tenant', 'landlord', 'shared'])).optional(),
  permittedUseAr: arText(500).optional(),
  requirementsAr: arText().optional(),
  notes: arText().optional(),
});

export const leaseCreateSchema = leaseFields
  .refine((v) => v.endDate >= v.startDate, {
    message: 'بداية العقد بعد نهايته',
    path: ['endDate'],
  })
  .refine((v) => v.ejarStatus !== 'registered' || Boolean(v.ejarContractNumber), {
    message: 'العقد المسجل في إيجار يحتاج رقم تسجيل',
    path: ['ejarContractNumber'],
  })
  .refine((v) => !v.hasTurnoverRent || v.turnoverRentPercent !== undefined, {
    message: 'حدد نسبة إيجار نسبة المبيعات',
    path: ['turnoverRentPercent'],
  });

/**
 * Correcting a lease after it was entered.
 *
 * `status` is not here: moving a lease between states has its own door
 * (`PATCH /leases/:id/status`) because it needs a date and a reason. The
 * cross-field rules are re-checked by the route against the MERGED row, since
 * a partial body cannot see the fields it does not carry.
 */
export const leaseUpdateSchema = leaseFields.omit({ status: true }).partial().merge(versioned);

export const leaseOptionUpdateSchema = z
  .object({
    optionType: z.enum(OPTION_TYPES),
    holder: z.enum(OPTION_HOLDERS),
    effectiveDate: isoDate.optional(),
    noticeWindowStart: isoDate.optional(),
    noticeWindowEnd: isoDate.optional(),
    noticeDays: z.number().int().min(0).max(1095).optional(),
    renewalTermMonths: z.number().int().min(1).max(600).optional(),
    renewalRentBasis: arText(300).optional(),
    conditionsAr: arText().optional(),
    notes: arText().optional(),
  })
  .partial()
  .merge(versioned);

/**
 * Moving a lease between states after it was created.
 *
 * Until 2026-09-24 there was no way to do this at all, so every status was
 * frozen at migration: بتيل read "expired" while trading, أساسيات الغذاء read
 * "terminated" with three years left to run. The reason is required because a
 * status change moves money between the rent roll and the arrears lists.
 */
export const LEASE_STATUS_TARGETS = ['active', 'holding_over', 'expired', 'terminated'] as const;
export const leaseStatusChangeSchema = z
  .object({
    status: z.enum(LEASE_STATUS_TARGETS),
    /** holding_over: the day it began. terminated: the termination date. */
    effectiveOn: isoDate.optional(),
    /** active: a renewal's new end date, when the term was extended. */
    endDate: isoDate.optional(),
    reasonAr: arRequired(500),
  })
  .merge(versioned)
  .refine((v) => !['holding_over', 'terminated'].includes(v.status) || v.effectiveOn, {
    message: 'التاريخ مطلوب لهذه الحالة',
    path: ['effectiveOn'],
  });

export const leaseOptionSchema = z.object({
  leaseId: z.string().min(1),
  optionType: z.enum(OPTION_TYPES),
  holder: z.enum(OPTION_HOLDERS),
  effectiveDate: isoDate.optional(),
  noticeWindowStart: isoDate.optional(),
  noticeWindowEnd: isoDate.optional(),
  noticeDays: z.number().int().min(0).max(1095).optional(),
  renewalTermMonths: z.number().int().min(1).max(600).optional(),
  renewalRentBasis: arText(300).optional(),
  conditionsAr: arText().optional(),
  notes: arText().optional(),
});

export const depositSchema = z.object({
  leaseId: z.string().min(1),
  partyId: z.string().min(1),
  amountHalalas: halalas.refine((v) => v > 0, 'مبلغ التأمين يجب أن يكون موجبًا'),
  heldSince: isoDate.optional(),
  status: z.enum(DEPOSIT_STATUSES).default('held'),
  instrument: z.enum(['cash', 'bank_guarantee', 'cheque', 'other']).default('cash'),
  instrumentRef: identifier.optional(),
  instrumentExpiresOn: isoDate.optional(),
  refundDueOn: isoDate.optional(),
  notes: arText().optional(),
});
export const depositUpdateSchema = depositSchema.omit({ leaseId: true }).partial().merge(versioned);

// ── Ledger ───────────────────────────────────────────────────────────────────

export const chargeCreateSchema = z.object({
  ledgerType: z.enum(LEDGER_TYPES),
  chargeType: z.enum(CHARGE_TYPES).default('base_rent'),
  tenantId: z.string().min(1),
  leaseId: z.string().optional(),
  propertyId: z.string().optional(),
  unitId: z.string().optional(),
  descriptionAr: arRequired(500),
  periodStart: isoDate.optional(),
  periodEnd: isoDate.optional(),
  dueDate: isoDate.optional(),
  amountHalalas: halalas,
  vatHalalas: halalas.default(0),
  notes: arText().optional(),
});

export const paymentCreateSchema = z.object({
  tenantId: z.string().min(1),
  propertyId: z.string().optional(),
  leaseId: z.string().optional(),
  reference: identifier,
  paymentDate: isoDate,
  valueDate: isoDate.optional(),
  method: z.enum(PAYMENT_METHODS).default('unknown'),
  status: z.enum(['pending', 'cleared', 'bounced', 'void']).default('pending'),
  /** Null is a real state: a reported payment of unknown amount. */
  amountHalalas: halalas.nullable().optional(),
  alreadyInBaseline: z.boolean().default(false),
  notes: arText().optional(),
});
export const paymentUpdateSchema = paymentCreateSchema.partial().merge(versioned);

/**
 * The opening settlement: every scheduled charge due on or before
 * `throughDate` on the chosen leases is closed against one offset payment per
 * lease. The written basis is required — it is the only record of why 19M of
 * schedule counts as collected without a receipt for each instalment.
 */
export const openingSettlementSchema = z.object({
  throughDate: isoDate,
  leaseIds: z.array(z.string().min(1)).min(1).max(200),
  evidenceAr: arRequired(1000).refine((v) => v.length >= 10, 'اكتب أساس التسوية بوضوح'),
});

/**
 * «تسجيل سداد» from the charges list: one payment, recorded and allocated to
 * one charge in a single action. The tenant, lease and property come from the
 * charge, so the body carries only what the person at the keyboard knows —
 * how much arrived, when, by what means, and under which bank reference.
 */
export const chargeSettleSchema = z.object({
  amountHalalas: halalas.min(1, 'المبلغ يجب أن يكون موجبًا'),
  paymentDate: isoDate,
  valueDate: isoDate.optional(),
  reference: identifier,
  method: z.enum(PAYMENT_METHODS).default('bank_transfer'),
  notes: arText().optional(),
});
export type ChargeSettleInput = z.infer<typeof chargeSettleSchema>;

export const allocationCreateSchema = z.object({
  paymentId: z.string().min(1),
  chargeId: z.string().min(1),
  amountHalalas: halalas.refine((v) => v > 0, 'مبلغ التخصيص يجب أن يكون موجبًا'),
  notes: arText().optional(),
});

export const allocationReverseSchema = z.object({
  reasonAr: arRequired(500),
});

// ── Review workflow ──────────────────────────────────────────────────────────

export const reviewTransitionSchema = z.object({
  reviewState: z.enum(REVIEW_STATES),
  approvalEvidence: arText(1000).optional(),
  version: z.number().int().min(1),
});

// ── Tasks ────────────────────────────────────────────────────────────────────

export const taskCreateSchema = z.object({
  taskType: z.enum(TASK_TYPES).default('task'),
  titleAr: arRequired(300),
  descriptionAr: arText().optional(),
  status: z.enum(TASK_STATUSES).default('open'),
  priority: z.enum(PRIORITIES).default('normal'),
  propertyId: z.string().optional(),
  unitId: z.string().optional(),
  leaseId: z.string().optional(),
  partyId: z.string().optional(),
  assigneeId: z.string().optional(),
  dueDate: isoDate.optional(),
  closureEvidenceAr: arText().optional(),
  nextActionAr: arText().optional(),
  quotedCostHalalas: halalas.optional(),
  actualCostHalalas: halalas.optional(),
  /**
   * What the task is about when that is not a property, lease or party —
   * a prospect, today. The columns have existed since the first migration
   * for reminder-generated tasks; «أنشئ مهمة» on a lead is the first screen
   * to write them, so the lead's page can list its own tasks.
   */
  relatedEntityType: z.enum(['prospect']).optional(),
  relatedEntityId: z.string().min(1).max(100).optional(),
  notes: arText().optional(),
});
export const taskUpdateSchema = taskCreateSchema.partial().merge(versioned);

// ── Documents and provenance ─────────────────────────────────────────────────

export const documentMetaSchema = z.object({
  category: z.enum(DOCUMENT_CATEGORIES).default('other'),
  notes: arText().optional(),
  isRestricted: z.boolean().default(false),
  sourceId: z.string().optional(),
  linkEntityType: z.string().max(50).optional(),
  linkEntityId: z.string().max(100).optional(),
  linkRole: z.string().max(50).optional(),
});
export const documentUpdateSchema = z
  .object({
    category: z.enum(DOCUMENT_CATEGORIES),
    notes: arText().optional(),
    isRestricted: z.boolean(),
    sourceId: z.string().optional(),
  })
  .partial()
  .merge(versioned);

export const documentLinkSchema = z.object({
  entityType: z.string().min(1).max(50),
  entityId: z.string().min(1).max(100),
  role: z.string().max(50).optional(),
});

export const documentFolderCreateSchema = z.object({
  nameAr: arRequired(120),
  parentId: z.string().max(100).optional(),
});
export const documentFolderUpdateSchema = z.object({ nameAr: arRequired(120) }).merge(versioned);

export const provenanceSchema = z.object({
  entityType: z.string().min(1).max(50),
  entityId: z.string().min(1).max(100),
  fieldName: z.string().max(100).optional(),
  sourceId: z.string().optional(),
  documentId: z.string().optional(),
  evidenceClass: z.enum(EVIDENCE_CLASSES),
  page: z.number().int().min(1).max(100000).optional(),
  sheet: z.string().max(200).optional(),
  rowRef: z.string().max(100).optional(),
  excerpt: arText(2000).optional(),
  notes: arText().optional(),
});

export const sourceCreateSchema = z.object({
  title: arRequired(300),
  sourceKind: z.enum(SOURCE_KINDS),
  evidenceClass: z.enum(EVIDENCE_CLASSES),
  sourceDate: isoDate.optional(),
  externalRef: z.string().max(500).optional(),
  notes: arText().optional(),
});

// ── Admin ────────────────────────────────────────────────────────────────────

export const userCreateSchema = z.object({
  email: z.string().email('البريد الإلكتروني غير صالح').max(254).toLowerCase(),
  displayName: arRequired(200),
  roles: z.array(z.enum(ROLES)).min(1, 'حدد دورًا واحدًا على الأقل'),
});

export const userUpdateSchema = z.object({
  displayName: arRequired(200).optional(),
  status: z.enum(['active', 'disabled', 'invited']).optional(),
  roles: z.array(z.enum(ROLES)).min(1).optional(),
  version: z.number().int().min(1),
});

// ── Query parameters ─────────────────────────────────────────────────────────

export const searchQuerySchema = z.object({
  q: z.string().trim().min(1).max(200),
  limit: z.coerce.number().int().min(1).max(200).optional(),
  entityTypes: z.string().optional(),
});

export const reportQuerySchema = z.object({
  propertyId: z.string().optional(),
  asOf: isoDate.optional(),
});

export const periodQuerySchema = z.object({
  from: isoDate,
  to: isoDate,
  propertyId: z.string().optional(),
});

export const listQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
  propertyId: z.string().optional(),
  status: z.string().max(50).optional(),
  q: z.string().trim().max(200).optional(),
});

// ── Charges: editing an existing due ─────────────────────────────────────────

/**
 * A charge may be edited, but never in the ways that would quietly move money:
 * `ledgerType` and `tenantId` are omitted, because changing either turns an
 * expected due into a historical one — or moves an arrear onto a different
 * tenant — while its allocations stay put.
 */
export const chargeUpdateSchema = chargeCreateSchema
  .omit({ ledgerType: true, tenantId: true })
  .partial()
  .extend({
    status: z.enum(['open', 'settled', 'written_off', 'disputed', 'void']).optional(),
    writtenOffHalalas: halalas.optional(),
    writeOffReasonAr: arText(500).optional(),
    invoiceNumber: identifier.optional(),
    invoiceIssuedOn: isoDate.optional(),
  })
  .merge(versioned);

// ── Expenses and service contracts ───────────────────────────────────────────

/**
 * `costClass` is the field that decides whether this touches NOI. Defaulting it
 * silently to `opex` would let a fit-out contribution depress the yield, so it
 * is required at the boundary even though the column carries a default.
 */
export const expenseCreateSchema = z.object({
  vendorId: z.string().min(1),
  propertyId: z.string().optional(),
  unitId: z.string().optional(),
  serviceContractId: z.string().optional(),
  invoiceNumber: identifier.optional(),
  invoiceDate: isoDate.optional(),
  descriptionAr: arRequired(500),
  category: z.enum(EXPENSE_CATEGORIES).default('other'),
  costClass: z.enum(COST_CLASSES),
  isRecoverable: z.boolean().default(false),
  netHalalas: halalas,
  vatHalalas: halalas.default(0),
  paidHalalas: halalas.default(0),
  dueDate: isoDate.optional(),
  status: z.enum(EXPENSE_STATUSES).default('open'),
  notes: arText().optional(),
});
export const expenseUpdateSchema = expenseCreateSchema
  .omit({ vendorId: true })
  .partial()
  .merge(versioned);

export const serviceContractCreateSchema = z.object({
  vendorId: z.string().min(1),
  propertyId: z.string().optional(),
  titleAr: arRequired(300),
  category: z.enum(EXPENSE_CATEGORIES).default('other'),
  contractNumber: identifier.optional(),
  startDate: isoDate,
  endDate: isoDate.optional(),
  annualValueHalalas: halalas.optional(),
  paymentFrequency: z.enum(PAYMENT_FREQUENCIES).default('monthly'),
  isRecoverable: z.boolean().default(true),
  autoRenews: z.boolean().default(false),
  noticeDays: z.number().int().min(0).max(1095).optional(),
  status: z.enum(['draft', 'active', 'expired', 'terminated']).default('active'),
  slaAr: arText().optional(),
  notes: arText().optional(),
});
export const serviceContractUpdateSchema = serviceContractCreateSchema
  .omit({ vendorId: true })
  .partial()
  .merge(versioned);

// ── Legal cases ──────────────────────────────────────────────────────────────

/**
 * Amounts are recorded AS REPORTED, with the reporting basis. The 2026-08-30
 * lawyer's report contains figures that do not reconcile and one case the
 * report itself excludes from its total; `excludedFromReportTotal` and
 * `amountWarningAr` exist so that survives into the system rather than being
 * silently averaged away.
 */
export const legalCaseCreateSchema = z.object({
  caseNumber: identifier.optional(),
  caseNumbers: z.array(identifier).max(20).optional(),
  titleAr: arRequired(300),
  summaryAr: arText().optional(),
  stage: z
    .enum([
      'pre_litigation',
      'filed',
      'first_instance',
      'appeal',
      'supreme',
      'execution',
      'settled',
      'closed',
      'withdrawn',
    ])
    .default('pre_litigation'),
  status: z.enum(['open', 'progress', 'closed', 'suspended']).default('open'),
  propertyId: z.string().optional(),
  counterpartyId: z.string().optional(),
  leaseId: z.string().optional(),
  court: arText(200).optional(),
  filedOn: isoDate.optional(),
  nextHearingDate: isoDate.optional(),
  /** Printed exactly as the report shows it. Never overwritten by a conversion. */
  deadlineHijriOriginal: z.string().trim().max(100).optional(),
  deadlineDate: isoDate.optional(),
  deadlineConversionStatus: z
    .enum(['converted', 'unverified', 'ambiguous', 'unconvertible', 'not_applicable'])
    .optional(),
  claimAmountHalalas: halalas.optional(),
  opposingClaimHalalas: halalas.optional(),
  acceptedClaimHalalas: halalas.optional(),
  rejectedClaimHalalas: halalas.optional(),
  excludedFromReportTotal: z.boolean().default(false),
  amountWarningAr: arText().optional(),
  courtVerified: z.boolean().default(false),
  nextActionAr: arText().optional(),
  lawyerName: arText(200).optional(),
  notes: arText().optional(),
});
export const legalCaseUpdateSchema = legalCaseCreateSchema.partial().merge(versioned);

export const legalCaseEventCreateSchema = z.object({
  caseId: z.string().min(1),
  eventDate: isoDate,
  eventType: z
    .enum(['note', 'filing', 'hearing', 'judgment', 'appeal', 'settlement', 'report_revision'])
    .default('note'),
  descriptionAr: arRequired(1000),
  sourceId: z.string().optional(),
});
export const legalCaseEventUpdateSchema = legalCaseEventCreateSchema
  .omit({ caseId: true })
  .partial()
  .merge(versioned);

// ── Data review (مراجعة البيانات) ────────────────────────────────────────────

export const dataIssueCreateSchema = z.object({
  issueCode: identifier.optional(),
  issueKind: z.enum(DATA_ISSUE_KINDS).default('conflict'),
  category: z.enum(DATA_ISSUE_CATEGORIES).default('other'),
  titleAr: arRequired(300),
  detailAr: arText().optional(),
  severity: z.enum(PRIORITIES).default('normal'),
  status: z.enum(DATA_ISSUE_STATUSES).default('open'),
  entityType: z.string().max(50).optional(),
  entityId: z.string().max(100).optional(),
  fieldName: z.string().max(100).optional(),
  /** Two claims that disagree, each with where it came from. */
  claimAAr: arText(1000).optional(),
  claimASourceId: z.string().optional(),
  claimBAr: arText(1000).optional(),
  claimBSourceId: z.string().optional(),
  differenceHalalas: z.number().int().optional(),
  nextActionAr: arText().optional(),
  sourceId: z.string().optional(),
  notes: arText().optional(),
});

/**
 * Closing a data issue needs evidence, the same rule tasks carry: "I looked at
 * it" is not a resolution. `accepted` needs it too — accepting an unreconciled
 * difference is a decision somebody has to own.
 */
export const dataIssueResolveSchema = z.object({
  status: z.enum(['resolved', 'accepted']),
  resolutionAr: arRequired(1000),
  version: z.number().int().min(1),
});

export const dataIssueUpdateSchema = dataIssueCreateSchema
  .partial()
  .omit({ status: true })
  .extend({
    /** A close proposed for a reviewer to make — see migration 0018. */
    proposedStatus: z.enum(['resolved', 'accepted']).nullable().optional(),
    proposedResolutionAr: arText(1000).nullable().optional(),
  })
  .merge(versioned);

// ── Inbox (بريد يحتاج متابعة) ────────────────────────────────────────────────

export const inboxItemCreateSchema = z.object({
  provider: z.enum(INBOX_PROVIDERS).default('manual'),
  account: z.string().trim().max(254).optional(),
  threadId: z.string().trim().max(200).optional(),
  subjectAr: arRequired(500),
  category: z.enum(INBOX_CATEGORIES).default('other'),
  status: z.enum(INBOX_STATUSES).default('open'),
  priority: z.enum(PRIORITIES).default('normal'),
  replyState: z.enum(INBOX_REPLY_STATES).default('unknown'),
  replyStateReasonAr: arText().optional(),
  lastMessageAt: z.string().trim().max(40).optional(),
  reviewedAt: isoDate.optional(),
  url: z.string().trim().max(1000).optional(),
  relatedEntityType: z.string().max(50).optional(),
  relatedEntityId: z.string().max(100).optional(),
  dueDate: isoDate.optional(),
  notes: arText().optional(),
});
export const inboxItemUpdateSchema = inboxItemCreateSchema.partial().merge(versioned);

/** Both null = unlink. One without the other is meaningless, so it is rejected. */
export const inboxRelinkSchema = z
  .object({
    entityType: z.enum(INBOX_LINK_ENTITY_TYPES).nullable(),
    entityId: z.string().trim().min(1).max(100).nullable(),
  })
  .merge(versioned)
  .refine((v) => (v.entityType === null) === (v.entityId === null), {
    message: 'حدّد نوع السجل ومعرّفه معًا، أو أزل الربط',
  });

export const inboxMessageCreateSchema = z.object({
  inboxItemId: z.string().min(1),
  messageId: z.string().trim().max(200).optional(),
  sentAt: z.string().trim().max(40).optional(),
  direction: z.enum(['incoming', 'outgoing']),
  fromAddress: z.string().trim().max(400).optional(),
  toAddress: z.string().trim().max(1000).optional(),
  isDraft: z.boolean().default(false),
  excerpt: arText(4000).optional(),
});

// ── Calendar, seasons and events ─────────────────────────────────────────────

/**
 * `nextDate` stays optional on purpose. Both Eids recur on a Hijri date whose
 * Gregorian equivalent depends on an official announcement, so the legacy data
 * carries no date for them — and a computed one would read as a commitment.
 * The refinement below is the rule that keeps that honest.
 */
export const calendarEventCreateSchema = z
  .object({
    titleAr: arRequired(300),
    eventKind: z.enum(EVENT_KINDS).default('other'),
    recurrenceCalendar: z.enum(RECURRENCE_CALENDARS).default('none'),
    recurrenceMonth: z.number().int().min(1).max(12).optional(),
    recurrenceDay: z.number().int().min(1).max(30).optional(),
    nextDate: isoDate.optional(),
    dateCertainty: z.enum(EVENT_DATE_CERTAINTY).default('unverified'),
    dateNoteAr: arText().optional(),
    propertyId: z.string().optional(),
    audience: z.enum(EVENT_AUDIENCES).default('owner_only'),
    proposalAr: arText().optional(),
    proposalStatus: z.enum(EVENT_PROPOSAL_STATUSES).default('none'),
    reminderOffsetsDays: z.array(z.number().int().min(0).max(365)).max(12).optional(),
    status: z.enum(['open', 'planned', 'done', 'cancelled']).default('open'),
    sourceId: z.string().optional(),
    notes: arText().optional(),
  })
  .refine((v) => v.recurrenceCalendar === 'none' || v.recurrenceMonth !== undefined, {
    message: 'التكرار يحتاج شهرًا',
    path: ['recurrenceMonth'],
  })
  .refine((v) => v.dateCertainty !== 'verified_official' || Boolean(v.nextDate), {
    message: 'تاريخ موثّق رسميًا يحتاج تاريخًا فعليًا',
    path: ['nextDate'],
  });

export const calendarEventUpdateSchema = z
  .object({
    titleAr: arRequired(300).optional(),
    eventKind: z.enum(EVENT_KINDS).optional(),
    recurrenceCalendar: z.enum(RECURRENCE_CALENDARS).optional(),
    recurrenceMonth: z.number().int().min(1).max(12).optional(),
    recurrenceDay: z.number().int().min(1).max(30).optional(),
    nextDate: isoDate.optional(),
    dateCertainty: z.enum(EVENT_DATE_CERTAINTY).optional(),
    dateNoteAr: arText().optional(),
    propertyId: z.string().optional(),
    audience: z.enum(EVENT_AUDIENCES).optional(),
    proposalAr: arText().optional(),
    proposalStatus: z.enum(EVENT_PROPOSAL_STATUSES).optional(),
    reminderOffsetsDays: z.array(z.number().int().min(0).max(365)).max(12).optional(),
    status: z.enum(['open', 'planned', 'done', 'cancelled']).optional(),
    sourceId: z.string().optional(),
    notes: arText().optional(),
  })
  .merge(versioned);

// ── Connectors ───────────────────────────────────────────────────────────────

export const connectorUpdateSchema = z
  .object({
    health: z.enum(CONNECTOR_HEALTH).optional(),
    account: z.string().trim().max(254).optional(),
    watchedPath: z.string().trim().max(500).optional(),
    enabled: z.boolean().optional(),
    notes: arText().optional(),
  })
  .merge(versioned);

// ── Provenance browsing ──────────────────────────────────────────────────────

export const provenanceQuerySchema = z.object({
  entityType: z.string().max(50).optional(),
  entityId: z.string().max(100).optional(),
  sourceId: z.string().max(100).optional(),
  evidenceClass: z.string().max(50).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export const sourceUpdateSchema = sourceCreateSchema.partial().merge(versioned);

// ── Market research agents ───────────────────────────────────────────────────

// ── Prospects ────────────────────────────────────────────────────────────────

/** The phone and email rules are the party-contact ones: E.164, and an
 *  address that can be an address. Converting a lead copies both onto the
 *  new party's contact, so they must already satisfy that table's CHECKs. */
const prospectPhone = z
  .string()
  .trim()
  .regex(/^\+[0-9]{6,20}$/, 'الجوال يجب أن يبدأ بـ + ثم أرقام فقط، مثل ‎+9665xxxxxxxx');
const prospectEmail = z.string().trim().toLowerCase().email('البريد الإلكتروني غير صالح').max(200);

export const prospectCreateSchema = z.object({
  nameAr: arRequired(200),
  companyAr: arText(300).optional(),
  activityAr: arText(200).optional(),
  phone: prospectPhone.optional(),
  email: prospectEmail.optional(),
  source: z.enum(PROSPECT_SOURCES).default('other'),
  sourceNoteAr: arText(300).optional(),
  propertyId: z.string().optional(),
  unitId: z.string().optional(),
  unitCategory: z.enum(UNIT_CATEGORIES).optional(),
  wantedAreaSqm: z.number().positive().max(100000).optional(),
  budgetAnnualHalalas: halalas.optional(),
  stage: z.enum(PROSPECT_STAGES).default('new'),
  lostReasonAr: arText(500).optional(),
  ownerId: z.string().optional(),
  nextActionAr: arText(300).optional(),
  nextActionDate: isoDate.optional(),
  notes: arText().optional(),
});
export const prospectUpdateSchema = prospectCreateSchema.partial().merge(versioned);

/**
 * One contact with a lead — and, in the same form, what comes next. A call
 * that ends without a next step is the usual way a lead goes quiet, so the
 * next action and the stage ride along rather than living in a second edit.
 */
export const prospectTouchSchema = z
  .object({
    touchedAt: isoDate,
    kind: z.enum(PROSPECT_TOUCH_KINDS).default('call'),
    summaryAr: arRequired(1000),
    nextActionAr: arText(300).optional(),
    nextActionDate: isoDate.optional(),
    stage: z.enum(PROSPECT_STAGES).optional(),
    lostReasonAr: arText(500).optional(),
  })
  .merge(versioned);

/**
 * «حوّل إلى مستأجر». Either an existing party (a returning tenant) or a new
 * one made from the lead's own fields; `partyForm` only matters for the
 * latter.
 */
export const prospectConvertSchema = z.object({
  partyId: z.string().min(1).optional(),
  partyForm: z.enum(PARTY_FORMS).default('company'),
});

export const RESEARCH_EFFORTS = ['low', 'medium', 'high'] as const;
/** The models a study may run on. A short list on purpose: each is a price. */
export const RESEARCH_MODELS = [
  'claude-opus-5-5',
  'claude-sonnet-5-5',
  'claude-haiku-5-5',
] as const;

export const researchStudyUpdateSchema = z
  .object({
    titleAr: arRequired(120),
    descriptionAr: arText(600),
    promptAr: arRequired(6000),
    model: z.enum(RESEARCH_MODELS),
    effort: z.enum(RESEARCH_EFFORTS),
    // US cents: $0.50 to $100 per run. The Anthropic cap, not an estimate.
    budgetCents: z.number().int().min(50).max(10000),
    intervalDays: z.number().int().min(1).max(90),
    nextRunDate: isoDate,
    runHour: z.number().int().min(0).max(23),
    enabled: z.boolean(),
  })
  .partial()
  .merge(versioned);
